/**
 * 「设定管理」未保存草稿的**离开守卫**。
 *
 * 背景（用户要求）：设定正文改成"右侧就是编辑页、可随意改、**不自动保存**"，
 * 于是"改动只活在组件 state 里"这件事变得危险 —— 切到别的设定、收起面板、
 * 回书架、刷新页面，任何一条路都会把改动丢掉。所以每次"要离开"之前必须问一句：
 * **保存 / 不保存 / 取消**（见 `components/modals/UnsavedChangesModal`）。
 *
 * 为什么单独一个模块、而不是全写在 `WorldSettings.tsx` 里：
 *
 * 1. **离开的动作不在面板内部**。收起面板是图标栏（`Editor/index.tsx` 的
 *    `togglePaneById`）、回书架是同一个文件的 `handleNavigateHome` —— 它们都不该
 *    认识"设定"这个业务。于是面板启动时**注册**一个守卫，发起方只问
 *    `confirmLeaveSettingsDraft(reason)`，拿到 `true` 才继续。弹窗仍由面板自己渲染
 *    （发起方 await 期间面板还没卸载，弹窗能正常显示）。
 *
 * 2. **桌面端面板可能在另一个窗口里**。第六版模型下：网页端与"主窗口最大化"时，
 *    设定管理渲在主窗口内部；其余情况它是**吸附窗口里的一整个 SPA 实例** ——
 *    两个窗口内存不共享，主窗口的注册表必然是空的。所以这里额外挂一条
 *    `BroadcastChannel`（同源，主窗口与吸附窗口共享）：本窗口没有面板时，
 *    **问对面窗口**有没有未保存的草稿；真有才把"问用户"这件事交给对面
 *    （弹窗必须出现在用户正在编辑的那个窗口里）。
 *
 *    ⚠️ 只在**本窗口一个面板都没有**时才问对面。这是安全的：面板同一时刻只可能在
 *    一处渲染（在窗口内、或在吸附窗口里，最大化时那个窗口已被关掉），所以
 *    "本窗口有面板"就等于"另一个窗口没有面板"。
 *
 * 3. 纯逻辑（注册表 + 决策 + 协议）留在 `.ts` 里，好让 vitest 单测锁住 ——
 *    仓库的 vitest 只收 `src/**\/*.test.ts`（`apps/web/vitest.config.ts`）。
 */

/** 用户对"有未保存改动，怎么办"的选择 */
export type LeaveDecision = 'save' | 'discard' | 'cancel';

/**
 * 离开的**原因** —— 只用来决定弹窗文案（"切换前 / 关闭前 / 离开创作页前"），
 * 逻辑上几种原因完全一样。
 */
export type SettingsLeaveReason = 'switch' | 'close-pane' | 'leave-page' | 'pane-move';

/** 面板注册进来的守卫：能回答"脏不脏"、能弹窗问用户、能真的保存 */
export interface SettingsDraftGuard {
  /** 此刻是否有未保存改动 */
  isDirty: () => boolean;
  /** 弹窗问用户（弹窗由面板渲染）；`cancel` = 不要离开 */
  ask: (reason: SettingsLeaveReason) => Promise<LeaveDecision>;
  /** 执行保存；返回 `true` = 已成功落库 */
  save: () => Promise<boolean>;
}

const localGuards = new Set<SettingsDraftGuard>();

/** 挂载的面板调用它注册守卫；返回值 = 卸载时注销 */
export function registerSettingsDraftGuard(guard: SettingsDraftGuard): () => void {
  localGuards.add(guard);
  ensureBridge();
  return () => {
    localGuards.delete(guard);
  };
}

/** 本窗口是否有未保存的设定草稿（同步，只看本窗口） */
export function hasUnsavedSettingsDraft(): boolean {
  for (const guard of localGuards) {
    if (guard.isDirty()) return true;
  }
  return false;
}

/**
 * 可以离开吗？
 *
 * - 本窗口挂着面板 → 问本窗口（这是网页端与"面板在主窗口内"的全部情况）；
 * - 本窗口没有面板（桌面端面板在吸附窗口里）→ 问另一个窗口；
 * - `remote: false` 表示发起方**已经知道**面板就在本窗口（省掉一次没有意义的跨窗询问，
 *   否则每次点图标栏都要白等一个超时）。
 */
export async function confirmLeaveSettingsDraft(
  reason: SettingsLeaveReason,
  options?: { remote?: boolean },
): Promise<boolean> {
  if (localGuards.size > 0) {
    for (const guard of Array.from(localGuards)) {
      if (!guard.isDirty()) continue;
      const decision = await guard.ask(reason);
      if (decision === 'cancel') return false;
      // 保存失败（网络/后端报错）→ 不能离开：否则用户以为存上了
      if (decision === 'save' && !(await guard.save())) return false;
    }
    return true;
  }

  if (options?.remote === false) return true;

  const bridge = ensureBridge();
  if (!bridge) return true;
  if (!(await bridge.askDirty())) return true;
  return bridge.askConfirm(reason);
}

// ── 跨窗口桥（桌面端：主窗口 ↔ 吸附窗口）──────────────────────────────────

/** 通道名导出给单测（测试里用它扮演"另一个窗口"） */
export const SETTINGS_DRAFT_CHANNEL = 'xiaotianshu:settings-draft';

/**
 * 问"对面有没有脏草稿"的等待上限。
 * 对面窗口根本没开时不希望每次点击都卡住，所以很短；**回答"有"之后**的等用户
 * 是另一回事（见 [`CONFIRM_TIMEOUT_MS`]）。
 */
const DIRTY_QUERY_TIMEOUT_MS = 400;

/**
 * 等用户拍板的上限。理论上可以无限等，但"对面窗口已经没了"时会让发起方的这次
 * 点击**永远没有结果**（没有任何反馈），所以给一个宽松的上限，超时按"允许"处理 ——
 * 宁可放行也不能把人卡死。
 */
const CONFIRM_TIMEOUT_MS = 120_000;

type BridgeMessage =
  | { kind: 'dirty-query'; id: string }
  | { kind: 'dirty-reply'; id: string; dirty: boolean }
  | { kind: 'confirm-request'; id: string; reason: SettingsLeaveReason }
  | { kind: 'confirm-reply'; id: string; allowed: boolean };

interface DraftBridge {
  askDirty: () => Promise<boolean>;
  askConfirm: (reason: SettingsLeaveReason) => Promise<boolean>;
}

let bridge: DraftBridge | null = null;
let bridgeCreated = false;

function newId(): string {
  const cryptoObj = globalThis.crypto as { randomUUID?: () => string } | undefined;
  return cryptoObj?.randomUUID?.() ?? `d-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * 首次需要时建通道（只建一次；环境不支持 `BroadcastChannel` 时返回 `null`，
 * 行为退化为"只有本窗口"—— 网页端本来就只有本窗口有面板）。
 */
function ensureBridge(): DraftBridge | null {
  if (bridgeCreated) return bridge;
  bridgeCreated = true;
  if (typeof BroadcastChannel === 'undefined') return null;

  const channel = new BroadcastChannel(SETTINGS_DRAFT_CHANNEL);
  // Node（vitest 的 environment: 'node'）里 BroadcastChannel 会吊住事件循环，
  // 能 unref 就 unref —— 否则单测跑完不退出。
  (channel as unknown as { unref?: () => void }).unref?.();

  const pending = new Map<string, {
    resolve: (value: boolean) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();

  const wait = (id: string, timeoutMs: number, onTimeout: boolean): Promise<boolean> => new Promise((resolve) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      resolve(onTimeout);
    }, timeoutMs);
    pending.set(id, { resolve, timer });
  });

  channel.onmessage = (event: MessageEvent) => {
    const message = event.data as BridgeMessage | undefined;
    if (!message || typeof message !== 'object') return;

    if (message.kind === 'dirty-query') {
      // 本窗口没有面板时如实回 false —— 这就是"面板不在这一侧"的正确答案
      channel.postMessage({ kind: 'dirty-reply', id: message.id, dirty: hasUnsavedSettingsDraft() });
      return;
    }

    if (message.kind === 'confirm-request') {
      // 对面先问过 dirty-query 才会走到这里，但状态可能已经变了（用户刚点了保存）
      void (async () => {
        const guard = Array.from(localGuards).find((g) => g.isDirty());
        let allowed = true;
        if (guard) {
          const decision = await guard.ask(message.reason);
          if (decision === 'cancel') allowed = false;
          else if (decision === 'save') allowed = await guard.save();
        }
        channel.postMessage({ kind: 'confirm-reply', id: message.id, allowed });
      })();
      return;
    }

    const entry = pending.get(message.id);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(message.id);
    entry.resolve(message.kind === 'dirty-reply' ? message.dirty : message.allowed);
  };

  bridge = {
    async askDirty() {
      const id = newId();
      // 没人应答 = 对面没有面板（或窗口已关）→ 按"不脏"处理
      const answer = wait(id, DIRTY_QUERY_TIMEOUT_MS, false);
      channel.postMessage({ kind: 'dirty-query', id });
      return answer;
    },
    async askConfirm(reason) {
      const id = newId();
      // 超时按"允许"处理：宁可放行，也不能因为对面窗口没了就让人点不动
      const answer = wait(id, CONFIRM_TIMEOUT_MS, true);
      channel.postMessage({ kind: 'confirm-request', id, reason });
      return answer;
    },
  };
  return bridge;
}
